import React from 'react';

/**
 * ErrorBoundary — last-resort React error catcher.
 *
 * Why this exists
 * ---------------
 * Some pages can throw synchronously during render on certain devices
 * (older Chrome on tablet, narrow viewports, race conditions on slow
 * cellular networks…). Without an error boundary React unmounts the whole
 * tree and the user sees a blank screen with no clue.
 *
 * This boundary keeps the Layout shell visible, surfaces the actual error
 * to the user (so they can screenshot it) and provides a "Réessayer" /
 * "Retour" recovery path. PC behaviour is unaffected as long as no error
 * is thrown.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Log to console so developers can find the trace easily.
        // We deliberately do NOT swallow the error — it stays in DevTools.
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary] caught:', error, info);
        this.setState({ info });
    }

    handleReload = () => {
        try { window.location.reload(); } catch { /* noop */ }
    };

    handleBack = () => {
        try {
            if (window.history.length > 1) window.history.back();
            else window.location.assign('/dashboard');
        } catch { /* noop */ }
    };

    render() {
        const { error, info } = this.state;
        if (!error) return this.props.children;

        const message = (error && (error.message || String(error))) || 'Erreur inconnue';
        const stack = (info && info.componentStack) || (error && error.stack) || '';

        return (
            <div
                style={{
                    minHeight: '70vh',
                    padding: '32px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    background: '#fff',
                    color: '#1B365D',
                }}
            >
                <div style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                    <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>
                        Une erreur s'est produite
                    </h2>
                    <p style={{ color: '#555', marginBottom: 20, fontSize: 14 }}>
                        La page n'a pas pu se charger correctement. Réessayez ou
                        revenez à l'écran précédent.
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
                        <button
                            onClick={this.handleReload}
                            style={{
                                background: '#1B365D', color: 'white', border: 0,
                                padding: '10px 18px', borderRadius: 8, fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Recharger la page
                        </button>
                        <button
                            onClick={this.handleBack}
                            style={{
                                background: 'white', color: '#1B365D',
                                border: '1px solid #1B365D',
                                padding: '10px 18px', borderRadius: 8, fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Retour
                        </button>
                    </div>
                    <details
                        style={{
                            textAlign: 'left',
                            background: '#FAFAFA',
                            border: '1px solid #EEE',
                            borderRadius: 8,
                            padding: 12,
                            fontSize: 12,
                            color: '#555',
                        }}
                    >
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                            Détail technique (à fournir au support)
                        </summary>
                        <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            <strong>{message}</strong>
                            {stack ? `\n${stack}` : ''}
                        </div>
                    </details>
                </div>
            </div>
        );
    }
}
